import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    Divider,
    IconButton,
    Link,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { isBefore } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import * as Yup from 'yup';

import { ICalendarEvent } from '@customTypes/calendar';
import { IGroup } from '@customTypes/group';
import { IUser } from '@customTypes/user';
import FormProvider, { RHFRadioGroup, RHFSelect, RHFTextField } from 'components/hook-form';
import Iconify from 'components/Iconify';
import { getGroups } from 'external/firebase/firestore/collections/groups';
import useLocales from 'hooks/useLocales';

import { PATH_DASHBOARD } from 'routes/paths';
import { convertDateToString, fDate } from 'utils/formatTime';

// ----------------------------------------------------------------------

export type CalendarCreateEditFormValues = Omit<ICalendarEvent, 'id' | 'title' | 'color'>;

type FormValues = Omit<CalendarCreateEditFormValues, 'meetingLinks'> & {
    meetingLinks: string;
};

type Props = {
    open: boolean;
    event: ICalendarEvent | null;
    range: {
        start: Date;
        end: Date;
    } | null;
    onClose: VoidFunction;
    onDeleteEvent: VoidFunction;
    onCreateUpdateEvent: (newEvent: CalendarCreateEditFormValues) => void;
    role?: IUser['role'];
};

// ----------------------------------------------------------------------

const LabelStyle = styled(Typography)(({ theme }) => ({
    ...theme.typography.subtitle1,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0),
}));

// ----------------------------------------------------------------------

export default function EventDialog({ open, event, role, onClose, onDeleteEvent, onCreateUpdateEvent, range }: Props) {
    const { translate, currentLang } = useLocales();
    const hasEventData = !!event;

    const [groups, setGroups] = useState<IGroup[]>([]);

    const LEARNING_MODE_OPTIONS = [
        { label: translate('online'), value: 'online' },
        { label: translate('live'), value: 'live' },
    ];

    const EventSchema = Yup.object().shape({
        groupId: Yup.string().required(translate('group_required_error')),
        mode: Yup.string().required(translate('mode_required_error')),
        address: Yup.string().when('mode', {
            is: 'live',
            then: Yup.string().required(translate('address_required_error')),
            otherwise: schema => schema,
        }),
        city: Yup.string().when('mode', {
            is: 'live',
            then: Yup.string().required(translate('city_required_error')),
            otherwise: schema => schema,
        }),
        start: Yup.date().required(translate('start_date_required_error')),
        end: Yup.date().required(translate('end_date_required_error')),
    });

    const defaultValues = useMemo(
        () => ({
            groupId: event?.groupId || '',
            mode: event?.mode || 'online',
            address: event?.address || '',
            city: event?.city || '',
            instructions: event?.instructions || '',
            meetingLinks: Array.isArray(event?.meetingLinks)
                ? event?.meetingLinks.join(', ')
                : event?.meetingLinks || '',
            meetingRecordings: event?.meetingRecordings || '',
            meetingInformation: event?.meetingInformation || '',
            start: event?.start || (range?.start ? new Date(range.start).toISOString() : new Date().toISOString()),
            end: event?.end || (range?.end ? new Date(range.end).toISOString() : new Date().toISOString()),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [event, range],
    );

    const methods = useForm<FormValues>({
        // @ts-ignore
        resolver: yupResolver(EventSchema),
        defaultValues,
    });

    const {
        reset,
        watch,
        control,
        handleSubmit,
        formState: { isSubmitting },
        setValue,
    } = methods;

    const values = watch();

    const isDateError = values.start && values.end ? isBefore(new Date(values.end), new Date(values.start)) : false;

    const onSubmit = async (data: FormValues) => {
        try {
            const newEvent: CalendarCreateEditFormValues = {
                groupId: data.groupId,
                mode: data.mode,
                address: data.address,
                city: data.city,
                instructions: data.instructions,
                meetingLinks: data.meetingLinks
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean),
                meetingRecordings: data.meetingRecordings,
                meetingInformation: data.meetingInformation,

                start: data.start,
                end: data.end,
            };

            onCreateUpdateEvent(newEvent);
            onClose();
            reset();
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        reset(defaultValues);
    }, [event, reset, defaultValues]);

    useEffect(() => {
        if (groups.length && values.groupId && (!hasEventData || event?.groupId !== values.groupId)) {
            const selectedGroup = groups.find(group => group.id === values.groupId);
            if (selectedGroup) {
                setValue('mode', selectedGroup.mode);
                setValue('address', selectedGroup.address || '');
                setValue('city', selectedGroup.city || '');
                setValue('instructions', selectedGroup.instructions || '');
                setValue('meetingLinks', (selectedGroup.meetingLinks?.monday || []).join(', '));
                setValue('meetingRecordings', selectedGroup.meetingRecordings || '');
                setValue('meetingInformation', selectedGroup.meetingInformation || '');
            }
        }
    }, [groups, values.groupId]);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const groups = await getGroups();
                setGroups(groups);
            } catch (err) {
                console.error(err);
            }
        };
        if (role === 'admin') {
            fetchGroups();
        }
    }, [event]);

    return (
        <Dialog fullWidth maxWidth='xs' open={open} onClose={onClose}>
            <DialogTitle>{event ? event.title : translate('event.new_event')}</DialogTitle>

            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                {role === 'admin' && (
                    <Stack spacing={3} sx={{ px: 3, mb: 6 }}>
                        <RHFSelect name='groupId' label={translate('group')}>
                            {groups.map(group => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.id} {group.name}
                                </MenuItem>
                            ))}
                        </RHFSelect>

                        <Controller
                            name='start'
                            control={control}
                            render={({ field }) => (
                                <MobileDateTimePicker
                                    {...field}
                                    onChange={newValue =>
                                        field.onChange(typeof newValue === 'string' ? new Date(newValue) : newValue)
                                    }
                                    label={translate('start_date')}
                                    renderInput={params => <TextField {...params} fullWidth />}
                                />
                            )}
                        />

                        <Controller
                            name='end'
                            control={control}
                            render={({ field }) => (
                                <MobileDateTimePicker
                                    {...field}
                                    onChange={newValue =>
                                        field.onChange(typeof newValue === 'string' ? new Date(newValue) : newValue)
                                    }
                                    label={translate('end_date')}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            error={isDateError}
                                            helperText={
                                                isDateError && translate('event.end_date_later_than_start_date')
                                            }
                                        />
                                    )}
                                />
                            )}
                        />

                        <Stack spacing={3} sx={{ mt: 6 }}>
                            <LabelStyle>{translate('learning_mode')}</LabelStyle>

                            <RHFRadioGroup spacing={1} name='mode' options={LEARNING_MODE_OPTIONS} />

                            {watch('mode') === 'live' && (
                                <>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 3 }}>
                                        <RHFTextField name='address' type='text' label={translate('address')} />
                                        <RHFTextField name='city' type='text' label={translate('city')} />
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 3 }}>
                                        <RHFTextField
                                            name='instructions'
                                            type='text'
                                            label={translate('instructions')}
                                        />
                                    </Stack>
                                </>
                            )}

                            {watch('mode') === 'online' && (
                                <>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 3 }}>
                                        <RHFTextField
                                            name='meetingLinks'
                                            type='text'
                                            label={translate('meeting_links')}
                                            placeholder={translate('comma_separated_links')}
                                        />
                                    </Stack>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mt: 3 }}>
                                        <RHFTextField
                                            name='meetingRecordings'
                                            type='text'
                                            label={translate('meeting_recordings')}
                                        />
                                        <RHFTextField
                                            name='meetingInformation'
                                            type='text'
                                            label={translate('meeting_information')}
                                        />
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </Stack>
                )}

                {event && (
                    <Stack spacing={3} sx={{ px: 3 }}>
                        <Typography variant='caption'>
                            {fDate(event.start, currentLang.value, 'PPP')} ⋅{' '}
                            {fDate(event.start, currentLang.value, 'p')} - {fDate(event.end, currentLang.value, 'p')}
                        </Typography>
                        {event?.instructions && (
                            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                {event.instructions}
                            </Typography>
                        )}
                        {event.mode === 'live' ? (
                            <>
                                <iframe
                                    height='250'
                                    referrerPolicy='no-referrer-when-downgrade'
                                    src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_MAPS_API_KEY}&q=${event?.address}`}
                                    allowFullScreen
                                ></iframe>
                                {event?.meetingInformation && (
                                    <Button
                                        variant='contained'
                                        href={event?.meetingInformation}
                                        component={Link}
                                        target='_blank'
                                        sx={{ mt: 2 }}
                                    >
                                        {translate('meeting_information')}
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                {event?.meetingLinks?.map((link, index) => (
                                    <Button
                                        key={index}
                                        variant='contained'
                                        href={link.startsWith('http') ? link : `https://${link}`}
                                        component={Link}
                                        target='_blank'
                                        sx={{ mb: 1 }}
                                    >
                                        {translate('join_zoom_meeting')}{' '}
                                        {(event?.meetingLinks?.length || 0) > 1 ? `#${index + 1}` : ''}
                                    </Button>
                                ))}
                                <Button
                                    variant='contained'
                                    href={event?.meetingRecordings || '/'}
                                    component={Link}
                                    target='_blank'
                                >
                                    {translate('meeting_recordings')}
                                </Button>
                                {event?.meetingInformation && (
                                    <Button
                                        variant='contained'
                                        href={event?.meetingInformation}
                                        component={Link}
                                        target='_blank'
                                    >
                                        {translate('meeting_information')}
                                    </Button>
                                )}
                            </>
                        )}
                        {(role === 'admin' || role === 'teacher') && (
                            <Button
                                variant='contained'
                                to={PATH_DASHBOARD.attendance.dailySheet(
                                    event.groupId,
                                    typeof event.start === 'string'
                                        ? event.start
                                        : convertDateToString(event.start || new Date()),
                                )}
                                component={RouterLink}
                            >
                                {translate('log_attendance')}
                            </Button>
                        )}
                    </Stack>
                )}

                <DialogActions>
                    {role === 'admin' && hasEventData && (
                        <Tooltip title={translate('delete')}>
                            <IconButton onClick={onDeleteEvent}>
                                <Iconify icon='eva:trash-2-outline' />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    <Button variant='outlined' color='inherit' onClick={onClose}>
                        {translate('close')}
                    </Button>

                    {role === 'admin' && (
                        <LoadingButton type='submit' variant='contained' loading={isSubmitting}>
                            {hasEventData ? translate('event.update') : translate('event.create')}
                        </LoadingButton>
                    )}
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
}
